import { Injectable } from '@angular/core';
import { EmployerService } from '../../deduction-lists/services/employer.service';
import { EmployerAccessEmployer } from '../../deduction-lists/models/employer.type';
import { TelemetryService } from '../../../shared/services/telemetry.service';

@Injectable({ providedIn: 'root' })
export class EmployerAccessService {
  private readonly accessCacheTtlMs = 60_000;
  private hasEmployerAccessKnown: boolean | null = null;
  private hasEmployerAccessPromise: Promise<boolean> | null = null;
  private lastAccessCheckAt: number | null = null;

  constructor(
    private employerService: EmployerService,
    private telemetryService: TelemetryService
  ) {}

  async ensureHasEmployerAccess(): Promise<boolean> {
    if (this.hasEmployerAccessKnown !== null && this.isCacheFresh()) {
      return this.hasEmployerAccessKnown;
    }

    if (this.hasEmployerAccessPromise) {
      return this.hasEmployerAccessPromise;
    }

    this.hasEmployerAccessPromise = (async () => {
      try {
        const accessResponse = await this.employerService.EmployerAccess();
        this.hasEmployerAccessKnown = this.resolveHasEmployerAccess(accessResponse);
        this.lastAccessCheckAt = Date.now();
        return this.hasEmployerAccessKnown;
      } catch (error) {
        this.telemetryService.trackException(error, {
          source: 'EmployerAccessService',
          step: 'ensureHasEmployerAccess',
          hasCachedAccess: String(this.hasEmployerAccessKnown !== null),
        });
        // Keep last known value on transient errors so the UI does not lose access state.
        if (this.hasEmployerAccessKnown !== null) {
          return this.hasEmployerAccessKnown;
        }

        return false;
      } finally {
        this.hasEmployerAccessPromise = null;
      }
    })();

    return this.hasEmployerAccessPromise;
  }

  clear(): void {
    this.hasEmployerAccessKnown = null;
    this.hasEmployerAccessPromise = null;
    this.lastAccessCheckAt = null;
  }

  private isCacheFresh(): boolean {
    if (this.lastAccessCheckAt === null) {
      return false;
    }

    return Date.now() - this.lastAccessCheckAt < this.accessCacheTtlMs;
  }

  private resolveHasEmployerAccess(accessResponse: EmployerAccessEmployer[] | unknown): boolean {
    if (!Array.isArray(accessResponse)) {
      return false;
    }

    return accessResponse.some((employer) => {
      const stateCode = employer?.stateCode;
      return stateCode === 0 || stateCode === '0';
    });
  }
}

